import type { UserSettings } from '@/entities/user';
import { useAuth } from '@/features/authentication';
import { userApi } from '@/features/profile-management';
import { StepPhone } from '@/features/registration';
import { settingsApi } from '@/features/user-settings';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Label } from '@/shared/ui/label';
import { Separator } from '@/shared/ui/separator';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { BlockedMembersCard } from '@/widgets/member-block';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { Bell, CheckCircle2, Eye, Loader2, Phone, Shield, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, logout, refreshUser } = useAuth();
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Legacy _user_settings_modify_phone: a new number must be validated by SMS
  // before it replaces the current one (the API refuses a number already
  // verified by another member).
  const [isChangingPhone, setIsChangingPhone] = useState(false);
  const handlePhoneVerified = async () => {
    setIsChangingPhone(false);
    await refreshUser();
    toast.success(t('settings.phone.updated'));
  };
  const [_isDeleting, _setIsDeleting] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.getSettings,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<UserSettings>) => settingsApi.updateSettings(data),
    onSuccess: () => {
      toast.success('Settings updated');
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: () => {
      toast.error('Failed to update settings');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: userApi.deactivateAccount,
    onSuccess: () => {
      toast.success('Account deactivated');
      logout();
      navigate('/');
    },
    onError: () => {
      toast.error('Failed to deactivate account');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: userApi.deleteAccount,
    onSuccess: () => {
      toast.success('Account deleted');
      logout();
      navigate('/');
    },
    onError: () => {
      toast.error('Failed to delete account');
    },
  });

  const handleToggle = (key: keyof UserSettings, value: boolean) => {
    updateMutation.mutate({ [key]: value });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-9 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <div className="space-y-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div>
                    <Skeleton className="h-6 w-32 mb-2" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-36" />
                    <Skeleton className="h-4 w-56" />
                  </div>
                  <Skeleton className="h-6 w-12 rounded-full" />
                </div>
                <Skeleton className="h-px w-full" />
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-36" />
                    <Skeleton className="h-4 w-56" />
                  </div>
                  <Skeleton className="h-6 w-12 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Paramètres</h1>
        <p className="text-muted-foreground mt-1">Gérez vos préférences de compte</p>
      </div>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-surface flex items-center justify-center">
              <Bell className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="font-display">Notifications</CardTitle>
              <CardDescription>Configurez la réception de vos notifications</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="emailNotifications" className="font-medium">
                Notifications par email
              </Label>
              <p className="text-sm text-muted-foreground">Recevez les notifications par email</p>
            </div>
            <Switch
              id="emailNotifications"
              checked={settings?.emailNotifications ?? true}
              onCheckedChange={(checked) => handleToggle('emailNotifications', checked)}
              disabled={updateMutation.isPending}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="pushNotifications" className="font-medium">
                Notifications push
              </Label>
              <p className="text-sm text-muted-foreground">
                Recevez les notifications sur votre appareil
              </p>
            </div>
            <Switch
              id="pushNotifications"
              checked={settings?.pushNotifications ?? true}
              onCheckedChange={(checked) => handleToggle('pushNotifications', checked)}
              disabled={updateMutation.isPending}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="messageNotifications" className="font-medium">
                Notifications de messages
              </Label>
              <p className="text-sm text-muted-foreground">
                Soyez averti(e) lorsque vous recevez de nouveaux messages
              </p>
            </div>
            <Switch
              id="messageNotifications"
              checked={settings?.messageNotifications ?? true}
              onCheckedChange={(checked) => handleToggle('messageNotifications', checked)}
              disabled={updateMutation.isPending}
            />
          </div>
        </CardContent>
      </Card>

      {/* Privacy */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Eye className="h-5 w-5 text-accent" />
            </div>
            <div>
              <CardTitle className="font-display">Confidentialité</CardTitle>
              <CardDescription>Contrôlez la visibilité de votre profil</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="showOnlineStatus" className="font-medium">
                Afficher le statut en ligne
              </Label>
              <p className="text-sm text-muted-foreground">
                Permettre aux autres de voir quand vous êtes en ligne
              </p>
            </div>
            <Switch
              id="showOnlineStatus"
              checked={settings?.showOnlineStatus ?? true}
              onCheckedChange={(checked) => handleToggle('showOnlineStatus', checked)}
              disabled={updateMutation.isPending}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="showLastSeen" className="font-medium">
                Afficher la dernière connexion
              </Label>
              <p className="text-sm text-muted-foreground">
                Afficher quand vous étiez dernièrement actif(ve)
              </p>
            </div>
            <Switch
              id="showLastSeen"
              checked={settings?.showLastSeen ?? true}
              onCheckedChange={(checked) => handleToggle('showLastSeen', checked)}
              disabled={updateMutation.isPending}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="profileVisible" className="font-medium">
                Profil visible
              </Label>
              <p className="text-sm text-muted-foreground">
                Rendre votre profil visible aux autres membres
              </p>
            </div>
            <Switch
              id="profileVisible"
              checked={settings?.profileVisible ?? true}
              onCheckedChange={(checked) => handleToggle('profileVisible', checked)}
              disabled={updateMutation.isPending}
            />
          </div>
        </CardContent>
      </Card>

      {/* Account */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-success/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-success" />
            </div>
            <div>
              <CardTitle className="font-display">Compte</CardTitle>
              <CardDescription>Gérez votre compte</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="py-2">
            <Label className="font-medium">Adresse email</Label>
            <p className="text-sm text-muted-foreground mt-1">{user?.email}</p>
          </div>
          <Separator />
          <div className="py-2" data-testid="settings-phone">
            <Label className="font-medium">{t('settings.phone.title')}</Label>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
              {user?.isPhoneVerified && user.phone ? (
                <>
                  <Phone className="h-4 w-4 text-primary" />
                  <span>{user.phone}</span>
                  <CheckCircle2
                    className="h-4 w-4 text-success"
                    aria-label={t('settings.phone.verified')}
                  />
                </>
              ) : (
                t('settings.phone.none')
              )}
            </p>
            <p className="text-sm text-muted-foreground mt-1 mb-3">
              {t('settings.phone.description')}
            </p>
            {isChangingPhone ? (
              <div className="space-y-4 rounded-lg border p-4">
                <StepPhone onVerified={handlePhoneVerified} />
                <Button variant="ghost" size="sm" onClick={() => setIsChangingPhone(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setIsChangingPhone(true)}>
                {t('settings.phone.change')}
              </Button>
            )}
          </div>
          <Separator />
          <div className="py-2">
            <Label className="font-medium">Changer le mot de passe</Label>
            <p className="text-sm text-muted-foreground mb-3">
              Mettez à jour votre mot de passe régulièrement pour une meilleure sécurité
            </p>
            <Button variant="outline">Changer le mot de passe</Button>
          </div>
        </CardContent>
      </Card>

      <BlockedMembersCard />

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-destructive/10 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <CardTitle className="font-display text-destructive">Zone de danger</CardTitle>
              <CardDescription>Actions irréversibles et destructives</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium">Désactiver le compte</p>
              <p className="text-sm text-muted-foreground">
                Masquer temporairement votre profil. Vous pourrez le réactiver à tout moment.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline">Désactiver</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display">
                    Désactiver le compte ?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Votre profil sera masqué aux autres utilisateurs. Vous pourrez réactiver votre
                    compte en vous reconnectant.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deactivateMutation.mutate()}
                    disabled={deactivateMutation.isPending}
                  >
                    {deactivateMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Désactiver
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          <Separator />
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium text-destructive">Supprimer le compte</p>
              <p className="text-sm text-muted-foreground">
                Supprimer définitivement votre compte et toutes vos données. Cette action est
                irréversible.
              </p>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">Supprimer le compte</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display">
                    Supprimer le compte ?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Cette action est irréversible. Votre profil, vos messages et toutes vos données
                    seront définitivement supprimés.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteMutation.isPending}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Supprimer le compte
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
